import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { SentryLogger } from '@fcom/core';
import {
  isEmpty,
  isFinnairNorraOrWetLease,
  MILLIS_IN_HOUR,
  TzDate,
  unsubscribe,
  mapErrorForSentry,
} from '@fcom/core/utils';
import { ProfileTier } from '@fcom/core-api/login';
import { profile } from '@fcom/core/selectors';
import { ButtonSize, ButtonTheme, NotificationTheme } from '@fcom/ui-components';
import { ElementActions, ElementTypes, GaContext } from '@fcom/common/';
import { GtmService } from '@fcom/common/gtm';
import {
  Cabin,
  FinnairCabinClass,
  FinnairItineraryItemFlight,
  FinnairItineraryItemType,
  FinnairTravelEndpoint,
  ItineraryItemFlight,
  ItineraryItemLayover,
} from '@fcom/dapi/api/models';
import { LanguageService } from '@fcom/ui-translate';
import { LocationInfoMap } from '@fcom/dapi';
import { FinnairBoundItemWithLocation } from '@fcom/common-booking';
import { ConfigService } from '@fcom/core/services';
import { OffersSeatService } from '@fcom/one-upgrade/api/services/offers-seat.service';
import { retryWithBackoff } from '@fcom/rx';
import { isFlight } from '@fcom/common-booking/utils/common-booking.utils';
import { BookingAppState, SelectionPhase } from '@fcom/common/interfaces/booking';
import { isShortHaulCountry } from '@fcom/common/utils/flight.utils';

import {
  lastRequestParams,
  selectedInboundItinerary,
  selectedOutboundItinerary,
  selectMultiCitySelectedItineraryDetails,
} from '../../../../store/selectors';

interface UpgradeFlight extends FinnairItineraryItemFlight {
  domestic?: boolean;
  tooCloseToDeparture?: boolean;
  tooCloseToWaitlist?: boolean;
  premiumEconomySeats?: number;
  businessSeats?: number;
  finnairFlight?: boolean;
  economyOnly?: boolean;
}

@Component({
  selector: 'fin-itinerary-upgrade-availability',
  templateUrl: './itinerary-upgrade-availability.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItineraryUpgradeAvailabilityComponent implements OnInit, OnDestroy {
  @Input()
  selectionPhase$: Observable<SelectionPhase>;
  @Input()
  locations: LocationInfoMap;
  @Input()
  bound: FinnairBoundItemWithLocation;
  @Input()
  classesForSegments: { [segmentId: string]: FinnairCabinClass[] };

  @Output()
  loginClicked: EventEmitter<void> = new EventEmitter();

  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly NotificationTheme = NotificationTheme;
  readonly ProfileTier = ProfileTier;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  profileTier$: Observable<ProfileTier>;
  contentUrl$: Observable<string>;
  waitContentUrl$: Observable<string>;
  notEligibleContentUrl$: Observable<string>;
  hasPartnerOrDomestic$: Observable<boolean>;
  hasWaitlisted$: Observable<boolean>;
  seatsOrWaitlistAvailable$: Observable<boolean>;
  upgradeFlights$ = new BehaviorSubject<UpgradeFlight[]>(undefined);
  tiersForUpgrade = [ProfileTier.GOLD, ProfileTier.LUMO, ProfileTier.PLATINUM];
  isBusinessOnly$: Observable<boolean>;
  eligible$: Observable<boolean>;
  displayError$ = new BehaviorSubject(false);
  showPremiumEconomy$: Observable<boolean>;

  private subscription = new Subscription();
  private CUTOFF_HOURS_WAITLIST = 40;
  private CUTOFF_HOURS_HEL = 3;
  private CUTOFF_HOURS_SHORT_HAUL = 24;
  private CUTOFF_HOURS_LONG_HAUL = 8;

  constructor(
    private store$: Store<BookingAppState>,
    private gtmService: GtmService,
    private languageService: LanguageService,
    private offersSeatService: OffersSeatService,
    private configService: ConfigService,
    private sentryLogger: SentryLogger
  ) {}

  ngOnInit(): void {
    this.showPremiumEconomy$ = this.store$.pipe(
      lastRequestParams(),
      map(
        ({ cabin }) =>
          this.configService.cfg.enableUpgradeAvailabilityPremiumEconomyInBooking && cabin !== Cabin.ECOPREMIUM
      )
    );
    this.profileTier$ = this.store$.pipe(
      profile(),
      take(1),
      map(({ tier }) => tier)
    );

    const boundFlights = this.bound.itinerary.filter(isFlight);
    this.isBusinessOnly$ = combineLatest([
      this.store$.pipe(lastRequestParams()),
      this.selectionPhase$,
      this.store$.pipe(selectedOutboundItinerary()),
      this.store$.pipe(selectedInboundItinerary()),
      this.store$.pipe(select(selectMultiCitySelectedItineraryDetails)),
    ]).pipe(
      map(([{ cabin }, phase, outItinerary, inItinerary, multicityBounds]) => {
        if (cabin === Cabin.BUSINESS) {
          return true;
        }
        const itinerary = this.getItinerary(phase, outItinerary, multicityBounds, inItinerary);
        const selectedOpened = boundFlights.every((flight: FinnairItineraryItemFlight) =>
          itinerary.some((it) => it.type === FinnairItineraryItemType.FLIGHT && it.id === flight.id)
        );

        return (
          !isEmpty(itinerary) &&
          selectedOpened &&
          itinerary.every((it) => ('cabinClass' in it ? it.cabinClass === FinnairCabinClass.BUSINESS : false))
        );
      })
    );

    const detailedFlights: UpgradeFlight[] = boundFlights.map((flight: FinnairItineraryItemFlight) => {
      const countryLocations = [
        this.locations[flight.departure.locationCode]?.countryCode,
        this.locations[flight.arrival.locationCode]?.countryCode,
      ];
      const domestic = countryLocations.every((code) => code === 'FI');
      const isShortHaul = countryLocations.every(isShortHaulCountry);
      const tooCloseToDeparture = this.flightTooCloseToDeparture(flight.departure, isShortHaul);
      const finnairFlight = isFinnairNorraOrWetLease(flight.operatingAirline.name, flight.operatingAirline.code);
      const classes = this.classesForSegments?.[flight.id] || [];
      const economyOnly = classes.length === 1 && classes[0] === FinnairCabinClass.ECONOMY;
      const tooCloseToWaitlist =
        new TzDate(flight.departure.dateTime).millis - Date.now() < this.CUTOFF_HOURS_WAITLIST * MILLIS_IN_HOUR;

      return {
        ...flight,
        domestic,
        tooCloseToDeparture,
        tooCloseToWaitlist,
        finnairFlight,
        isShortHaul,
        economyOnly,
      };
    });

    this.eligible$ = combineLatest([this.profileTier$, this.isBusinessOnly$]).pipe(
      map(([tier, businessOnly]) => this.tiersForUpgrade.includes(tier) && !businessOnly)
    );

    this.subscription.add(
      this.eligible$.pipe(filter(Boolean), take(1)).subscribe(() => this.fetchFlights(detailedFlights))
    );

    this.hasPartnerOrDomestic$ = this.upgradeFlights$.pipe(
      map((upgradeFlights) => upgradeFlights.some(({ domestic, finnairFlight }) => domestic || !finnairFlight))
    );

    this.hasWaitlisted$ = this.upgradeFlights$.pipe(
      map((flights) =>
        flights.some(
          ({ finnairFlight, domestic, tooCloseToDeparture, businessSeats, premiumEconomySeats, tooCloseToWaitlist }) =>
            finnairFlight &&
            !domestic &&
            !tooCloseToDeparture &&
            !tooCloseToWaitlist &&
            (businessSeats === 0 || premiumEconomySeats === 0)
        )
      )
    );
    this.seatsOrWaitlistAvailable$ = combineLatest([this.hasWaitlisted$, this.upgradeFlights$]).pipe(
      map(
        ([waitlisted, flights]) =>
          waitlisted ||
          flights.some(
            ({ premiumEconomySeats, businessSeats, tooCloseToDeparture, economyOnly }) =>
              !economyOnly && !tooCloseToDeparture && premiumEconomySeats + businessSeats > 0
          )
      )
    );

    this.contentUrl$ = this.contentUrlForFragment('fragments.itineraryAndUpgradesModalUpgradeAvailabilityAvailable');
    this.notEligibleContentUrl$ = this.contentUrlForFragment(
      'fragments.itineraryAndUpgradesModalUpgradeAvailabilityNotEligible'
    );
    this.waitContentUrl$ = this.contentUrlForFragment('fragments.itineraryAndUpgradesModalUpgradeAvailabilityWaitlist');
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
  }

  fetchFlights = (detailedFlights: UpgradeFlight[]): void => {
    const toRequest = detailedFlights
      .filter(
        ({ finnairFlight, domestic, tooCloseToDeparture, economyOnly }) =>
          !domestic && !tooCloseToDeparture && finnairFlight && !economyOnly
      )
      .map(({ departure, arrival, operatingAirline, flightNumber }) => ({
        boardPoint: departure.locationCode,
        offPoint: arrival.locationCode,
        departureDateTimeLocal: departure.dateTime,
        flightCarrierCode: operatingAirline.code,
        flightNumber: this.flightNumberWithoutCode(flightNumber, operatingAirline.code),
      }));

    this.gtmService.trackElement(
      `upgrade-availability_flights::eligible_${toRequest.length}::ineligible_${
        detailedFlights.length - toRequest.length
      }`,
      GaContext.BOOKING_FLOW,
      ElementTypes.MODAL,
      undefined,
      ElementActions.VIEW
    );

    if (toRequest.length === 0) {
      this.upgradeFlights$.next(detailedFlights);
      return;
    }

    this.subscription.add(
      this.offersSeatService
        .getSeatUpgradeOffersV2({
          body: {
            segmentInformation: toRequest,
          },
        })
        .pipe(retryWithBackoff(1))
        .subscribe({
          next: ({ segmentSeatInfo }) => {
            const flightsWithAvailability = detailedFlights.map((flight) => {
              const seatInfo = segmentSeatInfo.find(
                ({ segmentInformation }) =>
                  segmentInformation.flightNumber ===
                  this.flightNumberWithoutCode(flight.flightNumber, flight.operatingAirline.code)
              )?.seatInfo;

              const premiumEconomySeats = seatInfo?.find(({ upgradeClass }) => upgradeClass === 'F')?.seatsAvailable;
              const businessSeats = seatInfo?.find(({ upgradeClass }) => upgradeClass === 'U')?.seatsAvailable;
              return { ...flight, premiumEconomySeats, businessSeats };
            });

            const businessTotal = flightsWithAvailability.reduce((sum, { businessSeats }) => sum + businessSeats, 0);
            const premiumTotal = flightsWithAvailability.reduce(
              (sum, { premiumEconomySeats }) => sum + premiumEconomySeats,
              0
            );
            this.gtmService.trackElement(
              `upgrade-availability_business_${businessTotal}::premium_economy_${premiumTotal}`,
              GaContext.BOOKING_FLOW,
              ElementTypes.MODAL,
              undefined,
              ElementActions.VIEW
            );

            this.upgradeFlights$.next(flightsWithAvailability);
          },
          error: (error: unknown) => {
            this.displayError$.next(true);
            this.upgradeFlights$.next([]);
            this.sentryLogger.error('checking seat upgrade availability failed', {
              error: mapErrorForSentry(error),
            });
          },
        })
    );
  };

  getItinerary(
    phase: SelectionPhase,
    outItinerary: FinnairItineraryItemFlight[],
    multicityBounds: (ItineraryItemFlight | ItineraryItemLayover)[][],
    inItinerary: FinnairItineraryItemFlight[]
  ): ItineraryItemFlight[] | FinnairItineraryItemFlight[] {
    if (phase === SelectionPhase.OUTBOUND) {
      return outItinerary;
    } else if (phase === SelectionPhase.MULTICITY) {
      return multicityBounds.flat();
    } else {
      return inItinerary;
    }
  }

  contentUrlForFragment = (fragmentKey: string): Observable<string> =>
    this.languageService.translate(fragmentKey).pipe(
      take(1),
      map(({ url }) => url)
    );

  login(): void {
    this.gtmService.trackElement(
      'itinerary-upgrade-availability-login',
      GaContext.BOOKING_FLOW,
      ElementTypes.BUTTON,
      undefined,
      ElementActions.CLICK
    );
    this.loginClicked.emit();
  }

  private flightTooCloseToDeparture(departure: FinnairTravelEndpoint, isShortHaul: boolean): boolean {
    const cutoffHours =
      departure.locationCode === 'HEL'
        ? this.CUTOFF_HOURS_HEL
        : isShortHaul
          ? this.CUTOFF_HOURS_SHORT_HAUL
          : this.CUTOFF_HOURS_LONG_HAUL;
    return new TzDate(departure.dateTime).millis - Date.now() < cutoffHours * MILLIS_IN_HOUR;
  }

  private flightNumberWithoutCode(flightNumber: string, code: string): string {
    return parseInt(flightNumber.replace(code, ''), 10).toString();
  }
}
